import { createContext, useState, useMemo } from "react";
import { createTheme, darken, lighten } from '@mui/material/styles';
import { border } from "@mui/system";

export const tokens = (mode) => {
  // Declare your variable outside of the returned object
  const primary400BackgroundColors = mode === 'dark' ? "#1F2A40" : "#f2f0f0";

  // Return your tokens object based on the mode
  return mode === "dark"
    ? { // * Dark Mode Tokens
      grey: {
        100: "#fff",
        200: "#c2c2c2",
        300: "#a3a3a3",
        400: "#858585",
        500: "#666666",
        600: "#525252",
        700: "#3d3d3d",
        800: "#292929",
        900: "#141414",
      },
      primary: {
        100: "#d0d1d5",
        200: "#a1a4ab",
        300: "#727681",
        400: primary400BackgroundColors,
        500: "#141b2d",
        600: "#101624",
        700: "#0c101b",
        800: "#080b12",
        900: "#040509",
      },
      greenAccent: {
        100: "#dbf5ee",
        200: "#b7ebde",
        300: "#94e2cd",
        400: "#70d8bd",
        500: "#4cceac",
        600: "#3da58a",
        700: "#2e7c67",
        800: "#1e5245",
        900: "#0f2922",
      },
      redAccent: {
        100: "#f8dcdb",
        200: "#f1b9b7",
        300: "#e99592",
        400: "#e2726e",
        500: "#db4f4a",
        600: "#af3f3b",
        700: "#832f2c",
        800: "#58201e",
        900: "#2c100f",
      },
      blueAccent: {
        100: "#e1e2fe",
        200: "#c3c6fd",
        300: "#a4a9fc",
        400: "#868dfb",
        500: "#6870fa",
        600: "#535ac8",
        700: "#3e4396",
        800: "#2a2d64",
        900: "#151632",
      },
      yellowAccent: {
        100: "#fdf8d2",
        200: "#fbeeb1",
        300: "#f9e390",
        400: "#f6d96f",
        500: "#f4ce4e", // Main accent color
        600: "#a58229",
        700: "#7c611f",
        800: "#524114",
        900: "#29200a",
      },
      backgroundSecondary: {
        100: darken(primary400BackgroundColors, 0.2),
      },
    }
    : { // * Light Mode Tokens
      grey: {
        100: "#000000de",
        200: "#292929",
        300: "#3d3d3d",
        400: "#525252",
        500: "#666666",
        600: "#858585",
        700: "#a3a3a3",
        800: "#c2c2c2",
        900: "#e0e0e0",
      },
      primary: {
        100: "#040509",
        200: "#080b12",
        300: "#0c101b",
        400: primary400BackgroundColors,
        500: "#141b2d",
        600: "#1F2A40",
        700: "#727681",
        800: "#a1a4ab",
        900: "#d0d1d5",
      },
      greenAccent: {
        100: "#0f2922",
        200: "#1e5245",
        300: "#2e7c67",
        400: "#3da58a",
        500: "#4cceac",
        600: "#70d8bd",
        700: "#94e2cd",
        800: "#b7ebde",
        900: "#dbf5ee",
      },
      redAccent: {
        100: "#2c100f",
        200: "#58201e",
        300: "#832f2c",
        400: "#af3f3b",
        500: "#db4f4a",
        600: "#e2726e",
        700: "#e99592",
        800: "#f1b9b7",
        900: "#f8dcdb",
      },
      blueAccent: {
        100: "#151632",
        200: "#2a2d64",
        300: "#3e4396",
        400: "#535ac8",
        500: "#6870fa",
        600: "#868dfb",
        700: "#a4a9fc",
        800: "#c3c6fd",
        900: "#e1e2fe",
      },
      yellowAccent: {
        100: "#29200a",
        200: "#524114",
        300: "#7c611f",
        400: "#a58229",
        500: "#f4ce4e", // Main accent color
        600: "#f6d96f",
        700: "#f9e390",
        800: "#fbeeb1",
        900: "#fdf8d2",
      },
      backgroundSecondary: {
        100: lighten(primary400BackgroundColors, 0.2),
      },
    };
};

export const colors_per_mode = (mode) => {
  return mode === "dark"
    ? { // * Dark Mode
      primary: {
        main: "#828fb0",
        light: "#A6B7D9",
        dark: "#4F5D7F",
        contrastText: "#000000",
      },
      secondary: {
        main: "#797778",
        light: "#A6A6A6",
        dark: "#4D4D4D",
        contrastText: "#000000",
      },
      divider: {
        accent: "#13406c",
        contrastText: "#FFFFFF",
      },
      background: {
        paper: "#24273a", //"#121212", 
        default: "#14152a", //"#1E1E1E",
      },
      error: {
        main: "#EF5350",
        light: "#FFCDD2",
        dark: "#d32f2f",
        contrastText: "#000000",
      },
      success: {
        main: "#4CAF50",
        light: "#81C784",
        dark: "#388E3C",
        contrastText: "#000000",
      },
      warning: {
        main: "#FFEB3B",
        light: "#FFF176",
        dark: "#FBC02D",
        contrastText: "#000000",
      },
      info: {
        main: "#42A5F5",
        light: "#90CAF9",
        dark: "#1976D2",
        contrastText: "#000000",
      },
      text: {
        primary: "#FFFFFF",
        secondary: "#B0BEC5",
        disabled: "#7B7B7B",
      },
      common: {
        black: "#FFFFFF",
        white: "#000000",
      },
    }
    : { // * Light Mode
      primary: {
        main: "#4F5D7F",
        light: "#7d8aae",
        dark: "#243452",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#898788",
        light: "#b9b7b8",
        dark: "#5c5a5b",
        contrastText: "#FFFFFF",
      },
      divider: {
        accent: "#93bfec",
      },
      background: {
        paper: "#FFFFFF", //"#F5F5F5",
        default: "#efeff7", //"#FFFFFF",
      },
      error: {
        main: "#F44336",
        light: "#e57373",
        dark: "#d32f2f",
        contrastText: "#FFFFFF",
      },
      success: {
        main: "#13bb80",
        light: "#6fd0a6",
        dark: "#00875A",
        contrastText: "#FFFFFF",
      },
      warning: {
        main: "#F5F44B",
        light: "#f7f898",
        dark: "#f4df14",
        contrastText: "#000000",
      },
      info: {
        main: "#1685C9",
        light: "#33b2ef",
        dark: "#0e5493",
        contrastText: "#FFFFFF",
      },
      text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.6)",
        disabled: "rgba(0, 0, 0, 0.38)",
      },
      common: {
        black: "#000000",
        white: "#FFFFFF",
      },
    }
};


// mui theme settings using automatic color augmentation
export const themeSettings = (mode) => {
  const colors = colors_per_mode(mode);

  return {
    palette: {
      mode, // "light" or "dark"
      primary: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
        contrastText: colors.primary.contrastText,
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
        contrastText: colors.secondary.contrastText,
      },
      divider: colors.divider.accent,
      background: {
        paper: colors.background.paper,
        default: colors.background.default,
      },
      error: {
        main: colors.error.main,
        light: colors.error.light,
        dark: colors.error.dark,
        contrastText: colors.error.contrastText,
      },
      warning: {
        main: colors.warning.main,
        light: colors.warning.light,
        dark: colors.warning.dark,
        contrastText: colors.warning.contrastText,
      },
      info: {
        main: colors.info.main,
        light: colors.info.light,
        dark: colors.info.dark,
        contrastText: colors.info.contrastText,
      },
      success: {
        main: colors.success.main,
        light: colors.success.light,
        dark: colors.success.dark,
        contrastText: colors.success.contrastText,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.disabled,
      },
      common: {
        black: colors.common.black,
        white: colors.common.white,
      },
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: { fontFamily: ["Source Sans Pro", "sans-serif"].join(","), fontSize: 40 },
      h2: { fontFamily: ["Source Sans Pro", "sans-serif"].join(","), fontSize: 32 },
      h3: { fontFamily: ["Source Sans Pro", "sans-serif"].join(","), fontSize: 24 },
      h4: { fontFamily: ["Source Sans Pro", "sans-serif"].join(","), fontSize: 20 },
      h5: { fontFamily: ["Source Sans Pro", "sans-serif"].join(","), fontSize: 16 },
      h6: { fontFamily: ["Source Sans Pro", "sans-serif"].join(","), fontSize: 14 },
    },
    components: {
      MuiAlert: {
        defaultProps: {
          // only set variant when dark; in light we omit it so
          // MUI falls back to its built-in default (standard)
          ...(mode === 'dark' && { variant: 'outlined' }),
        },
        styleOverrides: {
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            // keep your desired background…
            backgroundColor: theme.palette.background.paper,
            // …but drop the gradient overlay
            backgroundImage: 'none',
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            '.MuiDialogTitle-root + &': {
              paddingTop: '20px',
            },
          })
        },
      },
      MuiBackdrop: { // * the shadow behind the dialog, dont k if shared with other components
        styleOverrides: {
          root: ({ theme }) => ({
            //  backgroundColor: "transparent",
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          /**
           * *MuiOutlinedInput-root: The root class for the input field.
           * *MuiInputLabel-outlined: The class for the label.
           * *MuiOutlinedInput-notchedOutline: The class for the border.    
           */
          root: ({ theme }) => ({
            // * Remove the web browser autofield custom sytle
            "& .MuiOutlinedInput - input:- webkit - autofill": {
              WebkitBoxShadow: `0 0 0 1000px transparent inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              transition: "background-color 5000s ease-in-out 0s",
            },
            "& .MuiInputBase-input:-webkit-autofill": {
              WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
              WebkitTextFillColor: `${theme.palette.text.main} !important`,
              transition: "background-color 5000s ease-in-out 0s",
            },
            // *Outlined variant styling
            "& .MuiOutlinedInput-root": {
              /*
              color: "#000",
              fontFamily: "Arial",
              fontWeight: "bold",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2e2e2e",
                borderWidth: "2px",
              },
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.secondary.main,
                  borderWidth: "3px",
                },
              },
              "&:hover:not(.Mui-focused)": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ccc",
                },
              },
              */
            },
            // //Label
            "& .MuiInputLabel-outlined": {
              //color: "#2e2e2e",
              //fontWeight: "bold",
              "&.Mui-focused": {
                color: theme.palette.primary.main,
              },
              '&.Mui-disabled': {
                color: theme.palette.primary.main,
              }
            },
            // *Filled variant styling
            //* Root class for the input field
            /*"& .MuiFilledInput-root": { 
              color: "#000",
              fontFamily: "Arial",
              fontWeight: "bold",
              backgroundColor: "#e7e7e7",
              borderTopLeftRadius: "7px",
              borderTopRightRadius: "7px",
              "&:before": { //* bottom border not focus
                borderColor: "#2e2e2e",
                borderWidth: "2px",
              },
              "&:after": { //* bottom border yes focus
                borderColor: "secondary.main",
                borderWidth: "3px",
                        },
              ":hover:not(.Mui-focused)": {
                "&:before": {
                  borderColor: "#e7e7e7",
                  borderWidth: "2px",
                },
                backgroundColor: "#f4f4f4",
              },
            },*/
            // //Class for the label of the filled input field
            "& .MuiInputLabel-filled": {
              //color: secondaryMain,
              //fontWeight: "bold",
              "&.Mui-focused": {
                color: theme.palette.primary.main,
              },
            },
            // *Standard variant styling
            /*"& .MuiInput-root": {
              color: "#000",
              fontFamily: "Arial",
              fontWeight: "bold",
              "&:before": {
                borderColor: "#2e2e2e",
                borderWidth: "2px",
              },
              "&:after": {
                borderColor: ,
                borderWidth: "3px",
              },
              ":hover:not(.Mui-focused)": {
                "&:before": {
                  borderColor: "#e7e7e7",
                  borderWidth: "2px",
                },
              },
            },*/
            // //Label
            "& .MuiInputLabel-standard": {
              //color: "#2e2e2e",
              //fontWeight: "bold",
              "&.Mui-focused": {
                color: theme.palette.primary.main,
              },
            }
          }),
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            background: theme.palette.background.paper,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderBottom: 'none',
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: 'normal',   // Allow wrapping instead of a single line
              lineHeight: '1.5',       // Adjust the line height as needed
              overflow: 'visible',     // Let the text be fully visible once wrapped
              textAlign: 'center',     // Optionally center-align your header titles
            },
            /*
            "& .MuiDataGrid-row.Mui-hovered .MuiButton-root:hover": {
              color: '{$(theme.palette.getContrastText(theme.palette.action.hover))} !important',
            },
            */
            "& .MuiDataGrid-toolbarContainer .MuiButton-root:hover": {
              color: `${theme.palette.text.primary} !important`,
              textDecoration: 'none !important',
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: ({ theme }) => ({
            //backgroundColor: theme.palette.background.paper,
            "& .MuiPaper-root": {
              background: theme.palette.background.paper,
            },
          })
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            //backgroundColor: theme.palette.background.paper,
            ":hover:not(.Mui-focused)": {
              backgroundColor: theme.palette.primary.main,
              color: `${theme.palette.getContrastText(theme.palette.action.hover)} !important`,
              "& .MuiSvgIcon-root ": {
                color: `${theme.palette.getContrastText(theme.palette.action.hover)} !important`,
              }
            }
            
          })
        }
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            // override the default <a> hover rule specifically on MUI Buttons
            '&.MuiButton-root:hover': {
              color: `${theme.palette.getContrastText(
                theme.palette.action.hover
              )} !important`,
              textDecoration: 'none !important',
            },
          }),
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: ({ theme }) => ({
          }),
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
          })
        }
      },
    },
  };
};
// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => { },
});

export const useMode = () => {
  const [mode, setMode] = useState("light"); // Default mode is dark
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prevMode) => (prevMode === "dark" ? "light" : "dark")),
    }),
    []
  );
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
